import { useState, useContext } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Grid,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import DownloadIcon from '@mui/icons-material/Download'
import CloseIcon from '@mui/icons-material/Close'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import dayjs from 'dayjs'
import axios from 'axios'
import { NotificationContext } from '../../../../contexts/notificationContext'
import CustomAlert from '../../../../components/CustomAlert/CustomAlert'

const RawDataDownload = ({ plantData, state }) => {
  const token = state?.token
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filterType, setFilterType] = useState('dateRange')
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'day'))
  const [endDate, setEndDate] = useState(dayjs())
  const [dataCollection, setDataCollection] = useState('plantinstantaneousdata') // New state for collection type

  // Replace notification state with alert state
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertSeverity, setAlertSeverity] = useState('info')
  const [exportType, setExportType] = useState('raw') // 'raw' or 'dashboard'

  const { addJobNotification } = useContext(NotificationContext)

  // Collection options
  const collectionOptions = [
    { value: 'energymeterinstantaneousdata', label: 'Energy Meter Raw Data' },
    { value: 'inverterinstantaneousdata', label: 'Inverter Raw Data' },
    { value: 'sensorinstantaneousdata', label: 'Sensor Raw Data' },
    { value: 'plantinstantaneousdata', label: 'Plant Instantaneous Data' },
    { value: 'plantdailyaggregation', label: 'Plant Daily Aggregation Data' },
    { value: 'plantmonthlyaggregation', label: 'Plant Monthly Aggregation Data' },
  ]

  const validateExportDates = (startDate, endDate, collection) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    if (!start.isBefore(end, 'day')) {
      return 'Start date must be before end date';
    }

   //add more if needed
    
    return null;
  };

  const openDialog = (type) => {
    setExportType(type)
    setOpen(true)
  }

  const handleClose = () => setOpen(false)
  // Add handler for alert close
  const handleAlertClose = () => setAlertOpen(false)

  // Handle start date change with validation
  const handleStartDateChange = (newStartDate) => {
    setStartDate(newStartDate)
    // If end date is not after the new start date, adjust it
    if (newStartDate && endDate && !endDate.isAfter(newStartDate, 'day')) {
      setEndDate(newStartDate.add(1, 'day'))
    }
  }
  // Handle end date change with validation
  const handleEndDateChange = (newEndDate) => {
    setEndDate(newEndDate)
    // If start date is not before the new end date, adjust it
    if (newEndDate && startDate && !startDate.isBefore(newEndDate, 'day')) {
      setStartDate(newEndDate.subtract(1, 'day'))
    }
  }

  const handleDownload = async () => {
    // Validate dates before proceeding
    const validationError = validateExportDates(startDate, endDate, dataCollection);
    if (validationError) {
      setAlertMessage(validationError);
      setAlertSeverity('error');
      setAlertOpen(true);
      return;
    }

    setLoading(true)
    const url = `https://staging-backend-v3-monitoring.de.energy/api/data/async`
    //const url = `http://localhost:6500/api/data/async`
    const deviceId = plantData.roofDetails[0].deviceId
    const plantId = plantData.roofDetails[0].plantId

    console.log('Starting export for collection:', dataCollection)

    const params = {
      deviceId,
      plantId,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      collection: dataCollection,
    }

    try {
      const response = await axios.get(url, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      })
      const { jobId } = response.data

      if (!jobId) throw new Error('No job ID returned from server')

      const collectionLabel =
        collectionOptions.find((opt) => opt.value === dataCollection)?.label ||
        dataCollection
      const jobTitle = `${collectionLabel} Export - ${deviceId} - ${startDate.format(
        'DD-MM-YYYY'
      )} to ${endDate.format('DD-MM-YYYY')}`

      addJobNotification({
        id: jobId,
        title: jobTitle,
        type: 'DATA_EXPORT',
        status: 'pending',
        progress: 0,
        message: `${collectionLabel} export job started`,
        severity: 'info',
        deviceId,
        created_at: new Date().toISOString(),
        plantName: plantData.plantName || 'Plant',
        checkEndpoint: `https://staging-backend-v3-monitoring.de.energy/api/job/${jobId}`,
      })
      // Update to use the new alert state
      setAlertMessage('Export job started. Check notifications for progress.')
      setAlertSeverity('success')
      setAlertOpen(true)
      handleClose()
    } catch (error) {
      console.error('Error starting instantaneous data export job:', error)
      // Update to use the new alert state
      setAlertMessage('Error starting export job. Please try again.')
      setAlertSeverity('error')
      setAlertOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const handleDashboardDownload = async () => {
    // Validate dates before proceeding
    const validationError = validateExportDates(startDate, endDate, dataCollection);
    if (validationError) {
      setAlertMessage(validationError);
      setAlertSeverity('error');
      setAlertOpen(true);
      return;
    }

    setLoading(true)
    const url = `https://staging-backend-v3-monitoring.de.energy/api/data/async`
    const deviceId = plantData.roofDetails[0].deviceId
    const plantId = plantData.roofDetails[0].plantId

    console.log('Starting export for collection:', dataCollection)

    const params = {
      deviceId,
      plantId,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      collection: dataCollection, // Add collection parameter
    }

    try {
      const response = await axios.get(url, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      })
      const jobId = response.data?.data?.jobId
      if (!jobId) throw new Error('No job ID returned from server')

      const collectionLabel =
        collectionOptions.find((opt) => opt.value === dataCollection)?.label ||
        dataCollection
      const jobTitle = `${collectionLabel} Export - ${deviceId} - ${startDate.format(
        'DD-MM-YYYY'
      )} to ${endDate.format('DD-MM-YYYY')}`

      addJobNotification({
        id: jobId,
        title: jobTitle,
        type: 'DATA_EXPORT',
        status: 'pending',
        progress: 0,
        message: `${collectionLabel} export job started`,
        severity: 'info',
        deviceId,
        created_at: new Date().toISOString(),
        plantName: plantData.plantName || 'Plant',
        checkEndpoint: `https://staging-backend-v3-monitoring.de.energy/api/job/${jobId}`,
      })
      // Update to use the new alert state
      setAlertMessage('Export job started. Check notifications for progress.')
      setAlertSeverity('success')
      setAlertOpen(true)
      handleClose()
    } catch (error) {
      console.error('Error starting instantaneous data export job:', error)
      // Update to use the new alert state
      setAlertMessage('Error starting export job. Please try again.')
      setAlertSeverity('error')
      setAlertOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const renderFilterOptions = () => {
    // Only render dateRange option since others are removed
    if (filterType === 'dateRange') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={
                  endDate
                    ? endDate.subtract(1, 'day')
                    : dayjs().subtract(1, 'day')
                }
                format="DD-MM-YYYY"
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={dayjs()}
                minDate={startDate ? startDate.add(1, 'day') : dayjs()}
                format="DD-MM-YYYY"
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      )
    }
    return null
  }

  return (
    <>
      {/* Button for both Dashboard data and Raw data */}
      <Box display="flex" gap={1}>
        {/* <div
          className="plant_list_h_button mr_8"
          onClick={() => openDialog('raw')}
        >
          <DownloadIcon className="mr_6" /> Raw Data
        </div> */}
        <div
          className="plant_list_h_button"
          onClick={() => openDialog('dashboard')}
        >
          <DownloadIcon className="mr_6" /> Dashboard Data
        </div>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {exportType === 'raw'
                ? 'Download Raw Data'
                : 'Download Dashboard Data'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Show collection dropdown */}
          <Box my={2}>
            <FormControl fullWidth>
              <InputLabel>Data Collection Type</InputLabel>
              <Select
                value={dataCollection}
                onChange={(e) => setDataCollection(e.target.value)}
                label="Data Collection Type"
              >
                {collectionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/*<Box my={2}>
            <FormControl fullWidth>
              <InputLabel>Filter Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Filter Type"
              >
                <MenuItem value="dateRange">Date Range</MenuItem>
              </Select>
            </FormControl>
          </Box>*/}

          <Box my={3}>{renderFilterOptions()}</Box>

          {loading && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              my={3}
            >
              <CircularProgress />
              <Typography variant="body2" color="textSecondary" mt={1}>
                Starting export job...
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={
              exportType === 'raw' ? handleDownload : handleDashboardDownload
            }
            disabled={loading}
            startIcon={<CloudDownloadIcon />}
          >
            {loading
              ? 'Starting...'
              : exportType === 'raw'
              ? 'Export Raw Data'
              : 'Export Dashboard Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CustomAlert */}
      <CustomAlert
        open={alertOpen}
        handleClose={handleAlertClose}
        message={alertMessage}
        severity={alertSeverity}
      />
    </>
  )
}

export default RawDataDownload
